import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import settlementService from '../services/settlementService';
import './CancellationApprovalsPage.css';

/**
 * Inbox for test-cancellation requests that exceeded their requester's
 * authority under centre_cancellation_policy (see SettlementPage's Cancel
 * Test modal, which is where these get raised). Approve actually performs
 * the cancellation (and any refund); the server re-checks that the caller
 * really is this request's centre's approver (or Administrator) before
 * doing anything — this inbox only decides what the person SEES.
 */
export const CancellationApprovalsPage = () => {
  const { can } = useAuth();
  const canApprove = can('CANCELLATION_APPROVALS', 'APPROVE');

  // KPI tile selected ('All' | 'Pending Approval' | 'Approved' | 'Rejected').
  // The inbox is small (only cancellations that exceeded the requester's authority),
  // so we fetch every status once per search/date filter and slice it client-side —
  // this lets every KPI tile show its live count even while another tile is active.
  const [activeStatus, setActiveStatus] = useState('Pending Approval');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [actioningId, setActioningId] = useState(null);
  const [remarksById, setRemarksById] = useState({});

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4500);
  };

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settlementService.getCancellationApprovalInbox({
        search: search.trim() || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        page: 1,
        pageSize: 200,
      });
      if (res.success && res.data) {
        setItems(res.data.items || []);
      }
    } catch (err) {
      console.error('Error fetching cancellation approval inbox:', err);
      showToast('Failed to load cancellation requests.', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, fromDate, toDate]);

  useEffect(() => {
    fetchInbox();
  }, [fetchInbox]);

  const counts = {
    All: items.length,
    'Pending Approval': items.filter((r) => r.status === 'Pending Approval').length,
    Approved: items.filter((r) => r.status === 'Approved').length,
    Rejected: items.filter((r) => r.status === 'Rejected').length,
  };

  const displayedItems = activeStatus === 'All' ? items : items.filter((r) => r.status === activeStatus);

  const testCount = (testIdsJson) => {
    try {
      const arr = JSON.parse(testIdsJson || '[]');
      return Array.isArray(arr) ? arr.length : 0;
    } catch {
      return 0;
    }
  };

  const handleApprove = async (requestId) => {
    setActioningId(requestId);
    try {
      const res = await settlementService.approveCancellationRequest(requestId, {
        remarks: remarksById[requestId]?.trim() || undefined,
      });
      if (res.success && res.data) {
        showToast(res.data.message || 'Request approved and tests cancelled.', 'success');
        fetchInbox();
      } else {
        showToast(res.message || 'Failed to approve request.', 'error');
      }
    } catch (err) {
      // 403 here means the server confirmed this caller is NOT this centre's
      // approver (or Administrator) — a real authority check, not just a UI gate.
      showToast(err.response?.data?.message || 'You are not authorized to approve this request.', 'error');
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (requestId) => {
    const reason = remarksById[requestId]?.trim();
    if (!reason) {
      showToast('Please enter a reason before rejecting.', 'warning');
      return;
    }
    setActioningId(requestId);
    try {
      const res = await settlementService.rejectCancellationRequest(requestId, { reason });
      if (res.success) {
        showToast('Cancellation request rejected. Nothing was cancelled.', 'success');
        fetchInbox();
      } else {
        showToast(res.message || 'Failed to reject request.', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'You are not authorized to act on this request.', 'error');
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="cxa-page">
      {toast && <div className={`cxa-toast cxa-toast-${toast.type}`}>{toast.message}</div>}

      <div className="cxa-header">
        <h1>🗂️ Cancellation Approvals</h1>
        <p className="cxa-subtitle">
          Test cancellations that went beyond their requester's authority (see Test Cancellation Policy) land here.
          Approving a request performs the cancellation now; rejecting it leaves the bill untouched.
        </p>
      </div>

      <div className="cxa-kpi-row">
        {[
          { key: 'All', label: 'All Requests', icon: '📋', cls: 'cxa-kpi-all' },
          { key: 'Pending Approval', label: 'Pending Approval', icon: '⏳', cls: 'cxa-kpi-pending' },
          { key: 'Approved', label: 'Approved', icon: '✅', cls: 'cxa-kpi-approved' },
          { key: 'Rejected', label: 'Rejected', icon: '⛔', cls: 'cxa-kpi-rejected' },
        ].map((tile) => (
          <button
            type="button"
            key={tile.key}
            className={`cxa-kpi-tile ${tile.cls} ${activeStatus === tile.key ? 'active' : ''}`}
            onClick={() => setActiveStatus(tile.key)}
          >
            <span className="cxa-kpi-icon">{tile.icon}</span>
            <span className="cxa-kpi-count">{counts[tile.key]}</span>
            <span className="cxa-kpi-label">{tile.label}</span>
          </button>
        ))}
      </div>

      <div className="cxa-filter-hub">
        <div className="cxa-filter-grid">
          <div className="cxa-field cxa-field-search">
            <label className="cxa-field-label" htmlFor="cxa-search-input">Search</label>
            <input
              id="cxa-search-input"
              type="text"
              className="cxa-field-input"
              placeholder="Lab No or Request No…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="cxa-field">
            <label className="cxa-field-label" htmlFor="cxa-from-date">From Date</label>
            <input
              id="cxa-from-date"
              type="date"
              className="cxa-field-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="cxa-field">
            <label className="cxa-field-label" htmlFor="cxa-to-date">To Date</label>
            <input
              id="cxa-to-date"
              type="date"
              className="cxa-field-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="cxa-field cxa-field-action">
            <label className="cxa-field-label cxa-field-label-hidden">Refresh</label>
            <button type="button" onClick={fetchInbox} className="cxa-btn-refresh">
              🔄 Refresh
            </button>
          </div>
        </div>
        <div className="cxa-filter-meta">
          Showing <strong>{displayedItems.length}</strong> of {items.length} request(s)
        </div>
      </div>

      {loading ? (
        <div className="cxa-empty">Loading…</div>
      ) : displayedItems.length === 0 ? (
        <div className="cxa-empty">No cancellation requests found for this filter.</div>
      ) : (
        <div className="cxa-list">
          {displayedItems.map((r, idx) => (
            <div key={r.requestId} className={`cxa-card cxa-status-${r.status.replace(/\s+/g, '').toLowerCase()}`}>
              <div className="cxa-card-top">
                <div>
                  <div className="cxa-title-row">
                    <span className="cxa-index-badge">#{idx + 1}</span>
                    <span className="cxa-request-no">{r.requestNo}</span>
                  </div>
                  <span className="cxa-labno">Lab No: <strong>{r.ledgerTransactionNo}</strong></span>
                  <span className="cxa-centre">{r.centreName}</span>
                </div>
                <span className={`cxa-status-badge st-${r.status.replace(/\s+/g, '').toLowerCase()}`}>{r.status}</span>
              </div>

              <div className="cxa-card-body">
                <div className="cxa-detail-row">
                  <span>Tests selected:</span> <strong>{testCount(r.testIdsJson)}</strong>
                </div>
                <div className="cxa-detail-row">
                  <span>Requested by:</span> <strong>{r.requestedByName}</strong> on {new Date(r.createdDate).toLocaleString()}
                </div>
                <div className="cxa-detail-row">
                  <span>Reason:</span> {r.cancellationReason || '—'}
                  {r.cancellationRemarks ? ` — ${r.cancellationRemarks}` : ''}
                </div>
                {r.refundAmount > 0 && (
                  <div className="cxa-detail-row">
                    <span>Refund if approved:</span> <strong>₹{r.refundAmount.toFixed(2)}</strong>
                  </div>
                )}
                {r.status === 'Approved' && (
                  <div className="cxa-detail-row cxa-resolved">✅ Approved by {r.approvedByName} on {new Date(r.approvedDate).toLocaleString()}</div>
                )}
                {r.status === 'Rejected' && (
                  <div className="cxa-detail-row cxa-resolved">⛔ Rejected by {r.rejectedByName} on {new Date(r.rejectedDate).toLocaleString()}</div>
                )}
              </div>

              {r.status === 'Pending Approval' && (
                <div className="cxa-card-actions">
                  {canApprove ? (
                    <>
                      <input
                        type="text"
                        placeholder="Remarks (required to reject)"
                        className="cxa-remarks-input"
                        value={remarksById[r.requestId] || ''}
                        onChange={(e) => setRemarksById((prev) => ({ ...prev, [r.requestId]: e.target.value }))}
                      />
                      <button
                        type="button"
                        className="cxa-btn-approve"
                        disabled={actioningId === r.requestId}
                        onClick={() => handleApprove(r.requestId)}
                      >
                        ✅ Approve &amp; Cancel
                      </button>
                      <button
                        type="button"
                        className="cxa-btn-reject"
                        disabled={actioningId === r.requestId}
                        onClick={() => handleReject(r.requestId)}
                      >
                        ⛔ Reject
                      </button>
                    </>
                  ) : (
                    <span className="cxa-no-authority-note">
                      You can view this request, but only this centre's approver (or Administrator) can act on it.
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CancellationApprovalsPage;
